// ReceiptFiling (truck stock, BL-203): a receipt a tech photographed on the road, for the office to file.
// The photo of the slip (PhotoThumb, opening full size in PhotoViewer) beside its facts as read (KeyValue);
// each line as read, what it matched in the catalog, its amount and where it goes (a job, onto his van,
// or the shop's overhead: a select); a line the catalog has no match for is the one-off part the tech
// kept it as, or found in the catalog; onto the van, it says what the van goes to. Then the sums
// (Totals), and what File does, in words, before it's done: the receipt's total is the cost of record,
// so its lines are never counted twice in a job's material. File says what's missing first (ink words
// after StateMark's red bar: red is never small text here) and takes the focus there; filed, it says
// what happened. At our cost (what the catalog says it costs, beside a line going onto the van), for
// whoever sees our cost. The tech reads his own, waiting for the office. Narrow (a SidePanel, a
// phone), the slip sits over its facts and where each line goes is under it, across the table, so the
// select is never scrolled out of sight. Reads only --receipt-*.
// New (Domain/Stock): its parts are PhotoThumb, PhotoViewer, KeyValue, LineTable and Totals; what's new
// is a receipt's rules (where each line goes, the one-off part, File checking them first).
import { useId, useState } from 'react';
import { money } from '../../format.ts';
import { Button } from '../Button/Button.tsx';
import { Choice, ChoiceGroup } from '../ChoiceGroup/ChoiceGroup.tsx';
import { SelectField, TextField } from '../Field/Field.tsx';
import { KeyValue } from '../KeyValue/KeyValue.tsx';
import { LineTable, type LineColumn, type LineRow } from '../LineTable/LineTable.tsx';
import { Link } from '../Link/Link.tsx';
import { Notice } from '../Notice/Notice.tsx';
import { StateMark } from '../StateMark/StateMark.tsx';
import { PhotoThumb, type Photo } from '../PhotoThumb/PhotoThumb.tsx';
import { PhotoViewer } from '../PhotoViewer/PhotoViewer.tsx';
import { Totals } from '../Totals/Totals.tsx';
import { qtyWords } from '../stock-words.ts';
import './ReceiptFiling.css';

export interface CatalogMatch {
  itemId: string;
  itemName: string;
  code: string;
  /** What the catalog says one costs: only for whoever sees our cost. */
  catalogEachCents?: number;
}
export interface ReceiptLine {
  id: string;
  /** As read off the slip: "PEX ELB 1/2 BRS LF". */
  text: string;
  qty: string;
  eachCents: number;
  /** What it matched in the catalog; null: nothing did. */
  match: CatalogMatch | null;
  /** What the tech kept an unmatched line as: "Dishwasher connector, ⅜ × ⅜, 60 in". */
  oneOff?: string;
  /** Where it goes as it opens: a destination's value. */
  to: string;
  /** On the van now, for a line that goes onto it. */
  vanOn?: string;
}
export interface ReceiptDestination {
  value: string;
  label: string;
  /** The van: only a line matched in the catalog goes onto it (it's counted there). */
  kind: 'job' | 'van' | 'shop';
}
export interface ReceiptFilingProps {
  /** "RC-1207". */
  id: string;
  supplier: string;
  /** "Mon 26 Oct, 10:05 a.m.", by "Sam K." */
  at: string;
  by: string;
  first: string;
  paid: string;
  /** The van: "Sam’s van". */
  place: string;
  /** The photo of the slip; none (not taken, or a PDF): its facts alone. */
  slip?: Photo;
  lines: ReceiptLine[];
  destinations: ReceiptDestination[];
  subtotalCents: number;
  gstCents: number;
  pstCents: number;
  totalCents: number;
  /** Finds parts in the catalog by name, code or part number. */
  find?: (words: string) => CatalogMatch[];
  /** The tech's own: read only. */
  readOnly?: boolean;
  /** Narrow (a SidePanel, a phone): the slip over its facts; where each line goes under it, not a column. */
  narrow?: boolean;
  initial?: {
    oneOff?: Record<string, 'keep' | 'find'>;
    find?: string;
    to?: Record<string, string>;
    tried?: boolean;
    filed?: boolean;
    photoOpen?: boolean;
  };
  /** Filed: where each line goes (a destination's value) and the catalog item each is (null: kept as a
   *  one-off part), by line. */
  onFile?: (filing: ReceiptFilingDecision) => void;
}

/** What File decided, by line: where it goes, and its catalog item (null: a one-off part). */
export interface ReceiptFilingDecision {
  to: Record<string, string>;
  items: Record<string, string | null>;
}

const nameOf = (m: CatalogMatch) => m.itemName.split(',')[0]!.toLowerCase();

export function ReceiptFiling(p: ReceiptFilingProps) {
  const { lines, destinations, readOnly, narrow, initial = {}, first, place } = p;
  const uid = useId();
  const [to, setTo] = useState<Record<string, string>>({
    ...Object.fromEntries(lines.map((l) => [l.id, l.to])),
    ...initial.to,
  });
  const [oneOff, setOneOff] = useState<Record<string, 'keep' | 'find'>>(initial.oneOff ?? {});
  const [picked, setPicked] = useState<Record<string, CatalogMatch>>({});
  const [words, setWords] = useState(initial.find ?? '');
  const [tried, setTried] = useState(!!initial.tried);
  const [filed, setFiled] = useState(!!initial.filed);
  const [open, setOpen] = useState(!!initial.photoOpen);
  const matchOf = (l: ReceiptLine) => l.match ?? picked[l.id] ?? null;
  const lock = readOnly || filed;
  const destLabel = (v: string) => destinations.find((d) => d.value === v)?.label ?? v;

  const undecided = lines.filter((l) => !matchOf(l) && oneOff[l.id] !== 'keep');
  const file = () => {
    setTried(true);
    const l = undecided[0];
    if (l) {
      document.getElementById(`${uid}-${l.id}`)?.querySelector<HTMLElement>('input')?.focus();
      return;
    }
    setFiled(true);
    p.onFile?.({
      to,
      items: Object.fromEntries(lines.map((l) => [l.id, matchOf(l)?.itemId ?? null])),
    });
  };

  // what File does, in words
  const jobs = new Map<string, ReceiptLine[]>();
  for (const l of lines) {
    const d = destinations.find((x) => x.value === to[l.id]);
    if (d?.kind === 'job') jobs.set(d.value, [...(jobs.get(d.value) ?? []), l]);
  }
  const sum = (ls: ReceiptLine[]) => ls.reduce((s, l) => s + Number(l.qty) * l.eachCents, 0);
  const van = lines.filter((l) => destinations.find((d) => d.value === to[l.id])?.kind === 'van');
  const shop = lines.filter((l) => destinations.find((d) => d.value === to[l.id])?.kind === 'shop');
  const says = [
    ...[...jobs].map(
      ([j, ls]) =>
        `${j} gets ${ls.length} ${ls.length === 1 ? 'line' : 'lines'} (${money(sum(ls))} before tax)`,
    ),
    ...(van.length
      ? [`${place} takes in ${van.map((l) => `${qtyWords(l.qty)} ${nameOf(matchOf(l)!)}s`).join(', ')}`]
      : []),
    ...(shop.length ? [`the shop’s overhead takes ${money(sum(shop))}`] : []),
  ].join('; ');

  const columns: LineColumn[] = [
    { id: 'line', label: 'Line', sub: 'as read' },
    { id: 'qty', label: 'Qty × each', align: 'end' },
    { id: 'amt', label: 'Amount', align: 'end' },
    ...(narrow ? [] : [{ id: 'to', label: 'Goes to' }]),
  ];
  const rows: LineRow[] = lines.map((l) => {
    const m = matchOf(l);
    const isVan = destinations.find((d) => d.value === to[l.id])?.kind === 'van';
    const found =
      !l.match && oneOff[l.id] === 'find' && words.trim() && p.find ? p.find(words.trim()).slice(0, 4) : [];
    const goes = lock ? (
      destLabel(to[l.id]!)
    ) : (
      <span className="fl-receipt__dest">
        <SelectField
          label={`${l.text}: goes to`}
          hideLabel
          value={to[l.id]!}
          onChange={(v) => setTo((t) => ({ ...t, [l.id]: v }))}
          options={destinations.map((d) => ({
            value: d.value,
            label: d.kind === 'van' && !m ? `${d.label} (a catalog part only)` : d.label,
            isDisabled: d.kind === 'van' && !m,
          }))}
        />
      </span>
    );
    const said =
      !m && !lock ? (
        <div className="fl-receipt__oneoff" id={`${uid}-${l.id}`}>
          <ChoiceGroup
            label={`${l.text}: a one-off part`}
            selectionMode="single"
            value={oneOff[l.id] ?? ''}
            onChange={(v) => setOneOff((o) => ({ ...o, [l.id]: v as 'keep' | 'find' }))}
          >
            <Choice value="keep">Keep it as a one-off part on {destLabel(to[l.id]!).split(',')[0]}</Choice>
            <Choice value="find">Find it in the catalog</Choice>
          </ChoiceGroup>
          {tried && !oneOff[l.id] ? (
            <span className="fl-receipt__bad">
              <StateMark state="problem" />
              Choose one
            </span>
          ) : null}
          {oneOff[l.id] === 'find' ? (
            <div className="fl-receipt__find">
              <TextField
                label="Find it"
                type="search"
                value={words}
                onChange={setWords}
                placeholder="Name, code or part number"
                error={tried && !picked[l.id] ? 'Pick one, or keep it as a one-off part' : undefined}
              />
              {words.trim() ? (
                found.length ? (
                  <ul className="fl-receipt__results" aria-label="Found in the catalog">
                    {found.map((f) => (
                      <li key={f.itemId} className="fl-receipt__result">
                        <span>
                          {f.itemName} <span className="fl-receipt__muted">{f.code}</span>
                        </span>
                        <Link onPress={() => setPicked((x) => ({ ...x, [l.id]: f }))}>Pick</Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="fl-receipt__muted">
                    Nothing in the catalog matches. Keep it as a one-off part, or add it on Catalog and prices
                    first.
                  </p>
                )
              ) : null}
            </div>
          ) : null}
        </div>
      ) : isVan && m && l.vanOn !== undefined ? (
        <>
          {readOnly ? 'Your' : `${place}:`} {qtyWords(l.vanOn)} → {qtyWords(Number(l.vanOn) + Number(l.qty))}{' '}
          {nameOf(m)}s
          {m.catalogEachCents !== undefined
            ? `, at ${money(l.eachCents)} each (the catalog says ${money(m.catalogEachCents)})`
            : ''}
          .
        </>
      ) : undefined;
    return {
      id: l.id,
      cells: {
        line: (
          <span className="fl-receipt__line">
            <span className="fl-receipt__read">{l.text}</span>
            {l.match ? (
              <span>
                {l.match.itemName} <span className="fl-receipt__muted">matched</span>
              </span>
            ) : picked[l.id] ? (
              <span>
                {picked[l.id]!.itemName} <span className="fl-receipt__muted">matched by the office</span>
              </span>
            ) : (
              <span>
                <span className="fl-receipt__strong">A one-off part</span>{' '}
                <span className="fl-receipt__muted">
                  {first} kept it as “{l.oneOff}”
                </span>
              </span>
            )}
          </span>
        ),
        qty: `${qtyWords(l.qty)} × ${money(l.eachCents)}`,
        amt: <strong>{money(Number(l.qty) * l.eachCents)}</strong>,
        ...(narrow ? {} : { to: goes }),
      },
      note: narrow ? (
        <div className="fl-receipt__goes">
          {lock ? <span>Goes to {goes}</span> : goes}
          {said}
        </div>
      ) : (
        said
      ),
    };
  });

  return (
    <div className="fl-receipt" data-narrow={narrow ? '' : undefined}>
      {filed ? (
        <Notice tone="done" takesFocus={!initial.filed} title={`Filed ${p.id}.`}>
          <p>{says.charAt(0).toUpperCase() + says.slice(1)}.</p>
        </Notice>
      ) : null}
      <div className="fl-receipt__head">
        {p.slip ? (
          <div className="fl-receipt__slip">
            <PhotoThumb photo={p.slip} size="tile" onPress={() => setOpen(true)} />
            <Link onPress={() => setOpen(true)}>Open the photo</Link>
          </div>
        ) : null}
        <KeyValue
          label={`${p.id} as read from the photo`}
          items={[
            { label: 'Supplier', value: p.supplier },
            { label: 'Bought', value: `${p.at}, by ${p.by}` },
            { label: 'Paid', value: p.paid },
            {
              label: 'Total',
              value: `${money(p.totalCents)} (${money(p.subtotalCents)} + GST ${money(p.gstCents)} + PST ${money(p.pstCents)})`,
            },
          ]}
        />
      </div>
      <LineTable label={`${p.id}’s lines`} columns={columns} rows={rows} />
      <div className="fl-receipt__sums">
        <Totals
          label={`${p.id}’s totals`}
          rows={[
            { label: 'Subtotal', value: money(p.subtotalCents) },
            { label: 'GST', value: money(p.gstCents) },
            { label: 'PST', value: money(p.pstCents) },
            { label: 'Total', value: money(p.totalCents), kind: 'strong' },
          ]}
        />
      </div>
      {readOnly ? (
        <Notice title="Waiting for the office to file it">
          <p>You sent it {p.at}.</p>
        </Notice>
      ) : filed ? null : (
        <>
          <p>
            Files {p.id} ({money(p.totalCents)}): {says}. The receipt’s total is the cost of record, so its
            lines aren’t counted twice in the job’s material.
          </p>
          <div className="fl-receipt__foot">
            <Button variant="primary" onPress={file}>
              File it
            </Button>
            <span className="fl-receipt__muted">{first} sees it filed.</span>
            {tried && undecided[0] ? (
              <span className="fl-receipt__problem" role="alert">
                <StateMark state="problem" />
                The {undecided[0].oneOff ?? undecided[0].text} is a one-off part: keep it as one, or find it
                in the catalog.
              </span>
            ) : null}
          </div>
        </>
      )}
      {p.slip ? (
        <PhotoViewer
          photos={[p.slip]}
          openId={open ? p.slip.id : null}
          onClose={() => setOpen(false)}
          label={`${p.id}’s photo`}
        />
      ) : null}
    </div>
  );
}
